import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  getObraPendencias,
  addObraPendencia,
  updateObraPendencia,
  setObraPendenciaStatus,
  deleteObraPendencia,
} from '@services/crud.js'
import type { ObraPendencia, ObraPendenciaStatus, ObraPendenciaPrioridade } from '@services/db.js'
import modulosJson from '@content/modulos.json'
import { FASES_CONSTRUTIVAS } from '@utils/constants.js'

export interface ObraPendenciaEnriquecida extends ObraPendencia {
  id: number
  faseLabel?: string
  topicoTitulo?: string
  moduloTitulo?: string
}

export interface FiltrosObraPendencias {
  busca: string
  status: 'ativas' | 'resolvidas' | 'todas'
  prioridade: '' | ObraPendenciaPrioridade
}

export interface NovaObraPendenciaInput {
  titulo: string
  descricao?: string
  prioridade?: ObraPendenciaPrioridade
  faseConstrutiva?: string
  topicoId?: string
  responsavel?: string
  prazo?: string
}

type ModuloRaw = {
  id: string
  titulo: string
  topicos: { id: string; titulo: string }[]
}

const TOPICO_LOOKUP: Record<string, { moduloTitulo: string; topicoTitulo: string }> = {}
for (const m of (modulosJson as { modulos: ModuloRaw[] }).modulos) {
  for (const t of m.topicos) {
    TOPICO_LOOKUP[t.id] = { moduloTitulo: m.titulo, topicoTitulo: t.titulo }
  }
}

const FASE_LABEL: Record<string, string> = Object.fromEntries(
  FASES_CONSTRUTIVAS.map((f) => [f.id, f.label]),
)

function enriquecer(p: ObraPendencia): ObraPendenciaEnriquecida {
  const ref = p.topicoId ? TOPICO_LOOKUP[p.topicoId] : undefined
  const faseLabel = p.faseConstrutiva ? FASE_LABEL[p.faseConstrutiva] : undefined
  return {
    ...p,
    id: p.id as number,
    ...(faseLabel ? { faseLabel } : {}),
    ...(ref?.topicoTitulo ? { topicoTitulo: ref.topicoTitulo } : {}),
    ...(ref?.moduloTitulo ? { moduloTitulo: ref.moduloTitulo } : {}),
  }
}

function filtrar(
  items: ObraPendenciaEnriquecida[],
  f: FiltrosObraPendencias,
): ObraPendenciaEnriquecida[] {
  return items.filter((i) => {
    if (f.status === 'ativas' && i.status === 'resolvida') return false
    if (f.status === 'resolvidas' && i.status !== 'resolvida') return false
    if (f.prioridade && i.prioridade !== f.prioridade) return false
    if (f.busca.trim()) {
      const q = f.busca.toLowerCase()
      const haystack = [i.titulo, i.descricao, i.responsavel, i.faseLabel, i.topicoTitulo]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      if (!haystack.includes(q)) return false
    }
    return true
  })
}

/** Lista operacional do canteiro — apenas tipo pendencia. */
export function useObraPendencias() {
  const [todos, setTodos] = useState<ObraPendenciaEnriquecida[]>([])
  const [filtros, setFiltros] = useState<FiltrosObraPendencias>({
    busca: '',
    status: 'ativas',
    prioridade: '',
  })
  const [isLoading, setIsLoading] = useState(true)

  const load = useCallback(async () => {
    const raw = await getObraPendencias()
    setTodos(raw.filter((p) => p.tipo === 'pendencia').map(enriquecer))
    setIsLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const itens = useMemo(() => filtrar(todos, filtros), [todos, filtros])

  const stats = useMemo(() => {
    const abertas = todos.filter((i) => i.status !== 'resolvida')
    return {
      total: todos.length,
      abertas: abertas.length,
      resolvidas: todos.filter((i) => i.status === 'resolvida').length,
      altaPrioridade: abertas.filter((i) => i.prioridade === 'alta').length,
    }
  }, [todos])

  async function criar(input: NovaObraPendenciaInput): Promise<void> {
    if (!input.titulo.trim()) return
    await addObraPendencia({
      tipo: 'pendencia',
      titulo: input.titulo.trim(),
      status: 'aberta',
      prioridade: input.prioridade ?? 'media',
      ...(input.descricao?.trim() ? { descricao: input.descricao.trim() } : {}),
      ...(input.faseConstrutiva ? { faseConstrutiva: input.faseConstrutiva } : {}),
      ...(input.topicoId ? { topicoId: input.topicoId } : {}),
      ...(input.responsavel?.trim() ? { responsavel: input.responsavel.trim() } : {}),
      ...(input.prazo ? { prazo: input.prazo } : {}),
    })
    await load()
  }

  async function editar(id: number, input: NovaObraPendenciaInput): Promise<void> {
    await updateObraPendencia(id, {
      titulo: input.titulo.trim(),
      ...(input.descricao !== undefined
        ? input.descricao.trim()
          ? { descricao: input.descricao.trim() }
          : { descricao: '' }
        : {}),
      ...(input.prioridade ? { prioridade: input.prioridade } : {}),
      ...(input.faseConstrutiva !== undefined ? { faseConstrutiva: input.faseConstrutiva } : {}),
      ...(input.responsavel !== undefined ? { responsavel: input.responsavel.trim() } : {}),
      ...(input.prazo !== undefined ? { prazo: input.prazo } : {}),
    })
    await load()
  }

  async function mudarStatus(id: number, status: ObraPendenciaStatus): Promise<void> {
    await setObraPendenciaStatus(id, status)
    await load()
  }

  async function remover(id: number): Promise<void> {
    await deleteObraPendencia(id)
    setTodos((prev) => prev.filter((i) => i.id !== id))
  }

  return {
    itens,
    todos,
    filtros,
    setFiltros,
    stats,
    isLoading,
    criar,
    editar,
    mudarStatus,
    remover,
    reload: load,
  }
}
